import { useCallback, useEffect, useState } from 'react';
import { esTokenDeImpersonacion } from '../utils/impersonation';
import getPasswordRequirement from '../api/login/getPasswordRequirement';

/**
 * ¿Hay que pedirle contraseña a la persona de esta sesión? Se pregunta al backend en cada
 * montaje (`GET /auth/password-requirement`), venga de donde venga la sesión: login, magic
 * link, `/token?token=` o recarga con la sesión abierta.
 *
 * 🔴 Falla EN ABIERTO: cualquier error (red, `success: false`, back viejo sin el endpoint)
 * deja `required` en `false` y la persona entra. Bloquear a alguien por un fallo de red es
 * peor que retrasarle la contraseña una sesión, y la siguiente carga vuelve a preguntar.
 *
 * 🔴 Sin caché en ningún storage: una respuesta guardada es una verdad congelada, que es
 * justo el bug que esto arregla.
 *
 * @returns required: si hay que mostrar el modal. markSatisfied: apagarlo en memoria cuando
 * la contraseña se ha establecido de verdad.
 */
export const usePasswordRequirement = (
	token: string
): { required: boolean; markSatisfied: () => void } => {
	const [required, setRequired] = useState(false);

	useEffect(() => {
		setRequired(false);

		// Soporte impersonando desde cuarteto nunca ve el modal: le pediría crear la
		// contraseña DEL CLIENTE. El backend también responde que no; esto ahorra la llamada.
		if (!token || esTokenDeImpersonacion(token)) return;

		let cancelled = false;

		getPasswordRequirement(token)
			.then((respuesta) => {
				if (!cancelled) setRequired(respuesta.required === true);
			})
			.catch((error) => {
				if (cancelled) return;
				console.error('No se pudo consultar si hay que establecer contraseña:', error);
				setRequired(false);
			});

		return () => {
			cancelled = true;
		};
	}, [token]);

	const markSatisfied = useCallback(() => setRequired(false), []);

	return { required, markSatisfied };
};
